import React, { useState, useEffect } from 'react'
import { Link } from 'react-router-dom'
import { memberService } from '../../services/memberService'
import api from '../../services/api'
import { showSuccess, showError, showWarning, showConfirm } from '../../utils/alert'

function Members() {
  const [members, setMembers] = useState([])
  const [loading, setLoading] = useState(true)
  const [search, setSearch] = useState('')
  const [levelFilter, setLevelFilter] = useState('')
  const [page, setPage] = useState(1)
  const [totalPages, setTotalPages] = useState(1)
  const [showExitModal, setShowExitModal] = useState(false)
  const [selectedMember, setSelectedMember] = useState(null)
  const [exitReason, setExitReason] = useState('')
  const [exiting, setExiting] = useState(false)
  const [upgrading, setUpgrading] = useState(false)
  const [showImportModal, setShowImportModal] = useState(false)
  const [importFile, setImportFile] = useState(null)
  const [importing, setImporting] = useState(false)
  const [importResult, setImportResult] = useState(null)

  useEffect(() => {
    fetchMembers()
  }, [page, search, levelFilter])

  const fetchMembers = async () => {
    try {
      setLoading(true)
      const response = await memberService.getAll({
        page,
        limit: 20,
        search,
        statusAnggota: levelFilter || undefined
      })
      setMembers(response.data.data || [])
      const meta = response.data.meta
      setTotalPages(meta?.totalPages || 1)
    } catch (error) {
      console.error('Error fetching members:', error)
    } finally {
      setLoading(false)
    }
  }

  const handleExit = async () => {
    if (!exitReason) {
      showWarning('Pilih alasan keluar')
      return
    }

    setExiting(true)
    try {
      await memberService.exit(selectedMember.NBA, { alasanKeluar: exitReason })
      showSuccess('Request validasi keluar berhasil dibuat', 'Menunggu persetujuan manager/admin.')
      setShowExitModal(false)
      setSelectedMember(null)
      setExitReason('')
      fetchMembers()
    } catch (error) {
      showError('Gagal membuat request validasi keluar', error.response?.data?.message)
    } finally {
      setExiting(false)
    }
  }

  const handleBulkUpgrade = async () => {
    const confirmed = await showConfirm(
      'Konfirmasi Bulk Upgrade',
      'Apakah Anda yakin ingin mengupgrade semua anggota Calon yang memenuhi syarat simpanan pokok menjadi Anggota Penuh?'
    )
    if (!confirmed) return

    setUpgrading(true)
    try {
      const response = await api.post('/members/bulk-upgrade')
      showSuccess(response.data.data.message)
      fetchMembers()
    } catch (error) {
      showError('Gagal melakukan bulk upgrade anggota', error.response?.data?.message)
    } finally {
      setUpgrading(false)
    }
  }

  const handleImport = async () => {
    if (!importFile) {
      showWarning('Pilih file Excel terlebih dahulu')
      return
    }

    setImporting(true)
    setImportResult(null)
    try {
      const formData = new FormData()
      formData.append('file', importFile)

      const response = await api.post('/members/import', formData, {
        headers: {
          'Content-Type': 'multipart/form-data'
        }
      })

      setImportResult(response.data.data)
      showSuccess(response.data.data.message)
      setShowImportModal(false)
      setImportFile(null)
      fetchMembers()
    } catch (error) {
      showError('Gagal mengimport anggota', error.response?.data?.message)
    } finally {
      setImporting(false)
    }
  }

  const formatDate = (dateStr) => {
    if (!dateStr) return '-'
    return new Date(dateStr).toLocaleDateString('id-ID')
  }

  const formatCurrency = (value) => {
    if (!value) return 'Rp 0'
    return new Intl.NumberFormat('id-ID', {
      style: 'currency',
      currency: 'IDR',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0
    }).format(Math.round(value))
  }

  return (
    <div>
      <div className="card">
        <div className="card-header">
          <h3 className="card-title">Data Anggota</h3>
          <div className="card-tools">
            <button
              className="btn btn-info btn-sm mr-1"
              onClick={() => setShowImportModal(true)}
            >
              <i className="fas fa-file-excel mr-1"></i> Import Excel
            </button>
            <button
              className="btn btn-success btn-sm mr-1"
              onClick={handleBulkUpgrade}
              disabled={upgrading}
            >
              <i className="fas fa-user-check mr-1"></i>
              {upgrading ? 'Mengupgrade...' : 'Upgrade Semua Calon ke Penuh'}
            </button>
            <Link to="/members/new" className="btn btn-primary btn-sm">
              <i className="fas fa-plus mr-1"></i> Tambah Anggota
            </Link>
          </div>
        </div>
        <div className="card-body">
          <div className="row mb-3">
            <div className="col-md-8">
              <div className="d-flex">
                <div className="input-group" style={{ maxWidth: 320 }}>
                  <input
                    type="text"
                    className="form-control"
                    placeholder="Cari nama atau NBA..."
                    value={search}
                    onChange={(e) => setSearch(e.target.value)}
                    onKeyPress={(e) => e.key === 'Enter' && setPage(1)}
                  />
                  <div className="input-group-append">
                    <button className="btn btn-default" onClick={() => setPage(1)}>
                      <i className="fas fa-search"></i>
                    </button>
                  </div>
                </div>
                <select
                  className="form-control ml-2"
                  style={{ maxWidth: 200 }}
                  value={levelFilter}
                  onChange={(e) => { setLevelFilter(e.target.value); setPage(1) }}
                >
                  <option value="">Semua Level</option>
                  <option value="Calon">Calon</option>
                  <option value="AnggotaPenuh">Anggota Penuh</option>
                  <option value="Keluar">Keluar</option>
                </select>
              </div>
            </div>
          </div>

          <div className="table-responsive">
            <table className="table table-bordered table-striped">
              <thead>
                <tr>
                  <th>NBA</th>
                  <th>Nama</th>
                  <th>Tgl Masuk</th>
                  <th>No. HP</th>
                  <th>Status</th>
                  <th>Simpanan Pokok</th>
                  <th>Simpanan Wajib</th>
                  <th>Aksi</th>
                </tr>
              </thead>
              <tbody>
                {loading ? (
                  <tr>
                    <td colSpan="8" className="text-center">
                      <i className="fas fa-spinner fa-spin mr-2"></i>
                      Memuat data...
                    </td>
                  </tr>
                ) : !Array.isArray(members) || members.length === 0 ? (
                  <tr>
                    <td colSpan="8" className="text-center">Tidak ada data</td>
                  </tr>
                ) : (
                  members.map((member) => (
                    <tr key={member.NBA}>
                      <td>{member.NBA}</td>
                      <td>{member.Nama}</td>
                      <td>{formatDate(member.TglMasuk)}</td>
                      <td>{member.MobileNo || '-'}</td>
                      <td>
                        <span className={`badge ${member.StatusAktif ? 'badge-success' : 'badge-warning'}`}>
                          {member.StatusAktif ? 'Aktif' : member.StatusAnggota}
                        </span>
                      </td>
                      <td>{formatCurrency(member.TotalSimpananPokok)}</td>
                      <td>{formatCurrency(member.TotalSimpananWajib)}</td>
                      <td>
                        <Link to={`/members/${member.NBA}`} className="btn btn-info btn-sm mr-1">
                          <i className="fas fa-eye"></i>
                        </Link>
                        <Link to={`/members/${member.NBA}/edit`} className="btn btn-warning btn-sm mr-1">
                          <i className="fas fa-edit"></i>
                        </Link>
                        {member.StatusAnggota !== 'Keluar' && (
                          <button
                            className="btn btn-danger btn-sm"
                            onClick={() => {
                              setSelectedMember(member)
                              setShowExitModal(true)
                            }}
                            title="Keluarkan Anggota"
                          >
                            <i className="fas fa-sign-out-alt"></i>
                          </button>
                        )}
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>

          <div className="d-flex justify-content-between align-items-center">
            <div>
              Halaman {page} dari {totalPages}
            </div>
            <div>
              <button
                className="btn btn-default btn-sm mr-1"
                onClick={() => setPage(p => Math.max(1, p - 1))}
                disabled={page === 1}
              >
                <i className="fas fa-chevron-left"></i>
              </button>
              <button
                className="btn btn-default btn-sm"
                onClick={() => setPage(p => Math.min(totalPages, p + 1))}
                disabled={page === totalPages}
              >
                <i className="fas fa-chevron-right"></i>
              </button>
            </div>
          </div>
        </div>
      </div>

      {showExitModal && selectedMember && (
        <div className="modal show" style={{ display: 'block', backgroundColor: 'rgba(0,0,0,0.5)' }}>
          <div className="modal-dialog">
            <div className="modal-content">
              <div className="modal-header">
                <h5 className="modal-title">Keluarkan Anggota</h5>
                <button type="button" className="close" onClick={() => setShowExitModal(false)}>
                  <span>&times;</span>
                </button>
              </div>
              <div className="modal-body">
                <div className="alert alert-info">
                  <i className="fas fa-info-circle mr-2"></i>
                  Request ini akan masuk ke menu validasi anggota keluar. Manager atau admin akan memvalidasi request ini sebelum anggota benar-benar dikeluarkan.
                </div>
                <p>Anda akan membuat request untuk mengeluarkan anggota:</p>
                <table className="table table-sm">
                  <tbody>
                    <tr>
                      <td><strong>NBA</strong></td>
                      <td>: {selectedMember.NBA}</td>
                    </tr>
                    <tr>
                      <td><strong>Nama</strong></td>
                      <td>: {selectedMember.Nama}</td>
                    </tr>
                    <tr>
                      <td><strong>Simpanan Pokok</strong></td>
                      <td>: {formatCurrency(selectedMember.TotalSimpananPokok)}</td>
                    </tr>
                    <tr>
                      <td><strong>Simpanan Wajib</strong></td>
                      <td>: {formatCurrency(selectedMember.TotalSimpananWajib)}</td>
                    </tr>
                    <tr>
                      <td><strong>Simpanan Sukarela</strong></td>
                      <td>: {formatCurrency(selectedMember.TotalSimpananSukarela)}</td>
                    </tr>
                  </tbody>
                </table>
                <div className="form-group">
                  <label>Alasan Keluar *</label>
                  <select
                    className="form-control"
                    value={exitReason}
                    onChange={(e) => setExitReason(e.target.value)}
                  >
                    <option value="">-- Pilih Alasan --</option>
                    <option value="Keluar">Keluar</option>
                    <option value="Meninggal">Meninggal</option>
                  </select>
                </div>
                {exitReason === 'Keluar' && (
                  <div className="alert alert-warning">
                    <small>
                      <i className="fas fa-exclamation-triangle mr-1"></i>
                      Anggota yang keluar akan dikenakan penalty sesuai pengaturan.
                    </small>
                  </div>
                )}
              </div>
              <div className="modal-footer">
                <button type="button" className="btn btn-secondary" onClick={() => setShowExitModal(false)}>
                  Batal
                </button>
                <button
                  type="button"
                  className="btn btn-danger"
                  onClick={handleExit}
                  disabled={exiting}
                >
                  {exiting ? 'Memproses...' : 'Buat Request Validasi'}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {showImportModal && (
        <div className="modal show" style={{ display: 'block', backgroundColor: 'rgba(0,0,0,0.5)' }}>
          <div className="modal-dialog">
            <div className="modal-content">
              <div className="modal-header">
                <h5 className="modal-title">Import Anggota dari Excel</h5>
                <button type="button" className="close" onClick={() => setShowImportModal(false)}>
                  <span>&times;</span>
                </button>
              </div>
              <div className="modal-body">
                <div className="alert alert-info">
                  <i className="fas fa-info-circle mr-2"></i>
                  Import data anggota dari file Excel (.xlsx atau .xls). Kolom yang kosong akan diisi dengan NULL.
                </div>
                <div className="form-group">
                  <label>File Excel *</label>
                  <input
                    type="file"
                    className="form-control"
                    accept=".xlsx,.xls"
                    onChange={(e) => setImportFile(e.target.files[0])}
                  />
                  <small className="text-muted">
                    Kolom yang wajib diisi: NBA, Nama, Tgl Masuk. Kolom lain bersifat opsional.
                  </small>
                </div>
                {importResult && (
                  <div className={`alert ${importResult.errorCount > 0 ? 'alert-warning' : 'alert-success'}`}>
                    <p><strong>Hasil Import:</strong></p>
                    <ul>
                      <li>Berhasil: {importResult.successCount}</li>
                      <li>Gagal: {importResult.errorCount}</li>
                    </ul>
                    {importResult.errorCount > 0 && importResult.errors && importResult.errors.length > 0 && (
                      <details>
                        <summary>Detail Error (max 10)</summary>
                        <div style={{ maxHeight: '300px', overflow: 'auto' }}>
                          {importResult.errors.map((error, idx) => (
                            <div key={idx} className="mb-2" style={{ borderBottom: '1px solid #dee2e6', paddingBottom: '5px' }}>
                              <strong>Error {idx + 1}:</strong> {error.error}
                              {error.row && (
                                <div className="text-muted" style={{ fontSize: '12px' }}>
                                  Row: {JSON.stringify(error.row)}
                                </div>
                              )}
                            </div>
                          ))}
                        </div>
                      </details>
                    )}
                  </div>
                )}
              </div>
              <div className="modal-footer">
                <button type="button" className="btn btn-secondary" onClick={() => {
                  setShowImportModal(false)
                  setImportFile(null)
                  setImportResult(null)
                }}>
                  Batal
                </button>
                <button
                  type="button"
                  className="btn btn-primary"
                  onClick={handleImport}
                  disabled={importing || !importFile}
                >
                  {importing ? 'Mengimport...' : 'Import'}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}

export default Members
